import React from "react"
import autobind from "class-autobind"
import { FloatingActionButton, CircularProgress, IconButton, FontIcon } from "material-ui"
import ContentAdd from "material-ui/svg-icons/content/add"
import strings from "../../../localization/strings"
import { IDate, IUserDocument, IExpenseDocument, ELoggedInUserType, IPayrollStatusDocument, EApproveStatus, EApproveType, EPayrollType } from "../../../models"
import PayPeriodSelector from "../../../components/payroll/PayPeriodSelector"
import moment from "moment"
import * as payGroupAndTypeHelper from "../../../helpers/payGroupAndTypeHelper"
import AddUserDetailExpenseDialog from "../../../components/expense/AddUserDetailExpenseDialog"
import * as api from "../../../api"
import AccountStore from "../../../stores/AccountStore"
import TableActionBar from "../../../components/layout/TableActionBar"
import AppStateStore from "../../../stores/AppStateStore"
import UserDetailExpensesTable from "../../../components/expense/UserDetailExpensesTable"
import * as utils from "../../../helpers/utils"
import { grey700 } from "material-ui/styles/colors"
import * as permissionHelper from "../../../helpers/permissionHelper"
import AuthenticationStore from "../../../stores/AuthenticationStore"
import * as expenseActions from "../../../actions/ExpenseActions"
import * as payrollStatusHelper from "../../../helpers/payrollStatusHelper"

interface IProps {
    loggedInUserType: ELoggedInUserType
    user: IUserDocument
    readonly: boolean
}
interface IState {
    loading: boolean
    payPeriodStatus: IPayrollStatusDocument
    referenceDate: IDate
    openAddExpenseDialog: boolean
    editExpense: IExpenseDocument
    expenses: IExpenseDocument[]
    selectedExpenses: IExpenseDocument[]
}

export default class UserDetailExpenses extends React.Component<IProps, IState> {
    private employeePayGroupId
    constructor(props: IProps) {
        super(props)
        this.state = {
            loading: false,
            payPeriodStatus: undefined,
            referenceDate: moment().format(),
            openAddExpenseDialog: false,
            editExpense: null,
            expenses: [],
            selectedExpenses: []
        }
        autobind(this)
        this.employeePayGroupId = payGroupAndTypeHelper.getPayGroupIdByPayrollPayGroupData({
            PayrollHierarchyItemId: props.user.PayrollHierarchyItemId,
            PayrollPayGroupId: props.user.PayrollPayGroupId
        })
    }

    componentDidMount() {
        this.getAllData()
    }

    getAllData() {
        this.setState({ loading: true, payPeriodStatus: undefined }, () => {
            this.getExpenses()
            this.getPayPeriodStatus()
        })
    }

    getExpenses(msg?: string) {
        api.getEmployeeExpenses(this.props.user.Id, moment(this.state.referenceDate).utc(true).format()).then(res => {
            msg &&
                AppStateStore.setProgressSnackbarContent(msg)
            this.setState({
                loading: false,
                expenses: res.data,
                selectedExpenses: [],
            })
        })
    }

    getPayPeriodStatus() {
        api.getPayperiodStatus(AccountStore.getCurrentAccount().Id, { PayrollHierarchyItemId: this.props.user.PayrollHierarchyItemId, PayrollPayGroupId: this.props.user.PayrollPayGroupId }, moment(this.state.referenceDate).utc(true).format(), EPayrollType.Expenses).then(res =>
            this.setState({ payPeriodStatus: res.data })
        )
    }

    handleUpdateApproveStatus(status: EApproveStatus) {
        const sameStatusCount = this.state.selectedExpenses.filter(item =>
            this.props.loggedInUserType === ELoggedInUserType.Employee ?
                status === EApproveStatus.Approved ?
                    item.EmployeeApproved :
                    !item.EmployeeApproved :
                status === EApproveStatus.Approved ?
                    item.SupervisorApproved :
                    !item.SupervisorApproved
        ).length

        this.props.loggedInUserType === ELoggedInUserType.Employee && this.state.selectedExpenses.filter(item => item.SupervisorApproved).length > 0 ?
            AppStateStore.setProgressSnackbarContent(strings.Cannot_approve_unapprove_the_approved_expense_by_supervisor) :
            sameStatusCount > 0 ?
                AppStateStore.setProgressSnackbarContent(status === EApproveStatus.Approved ?
                    strings.Cannot_approve_the_approved_expense :
                    strings.Cannot_unapprove_the_unapproved_expense
                ) :
                AppStateStore.setConfirmDialogContent({
                    content: status === EApproveStatus.Approved ?
                        strings.Are_you_sure_you_want_to_approve_the_selected_expense :
                        strings.Are_you_sure_you_want_to_unapprove_the_selected_expense,
                    confirm: () => {
                        const approveType: EApproveType =
                            this.props.loggedInUserType === ELoggedInUserType.Employee ?
                                status === EApproveStatus.Approved ?
                                    EApproveType.EmployeeApproved :
                                    EApproveType.EmployeeUnapproved :
                                status === EApproveStatus.Approved ?
                                    EApproveType.SupervisorApproved :
                                    EApproveType.SupervisorUnapproved
                        api.updateExpensesApproveStatus(this.state.selectedExpenses.map(item => item.Id), approveType).then(() => {
                            AppStateStore.setProgressSnackbarContent(status === EApproveStatus.Approved ?
                                strings.successfully_approved :
                                strings.successfully_unapproved
                            )
                            this.getExpenses()
                        })
                    }
                })
    }

    render() {
        const locked = this.state.payPeriodStatus === undefined ||
            payrollStatusHelper.isLocked([this.state.payPeriodStatus], this.props.user.Id, this.props.loggedInUserType)
        const readonly = locked || (this.props.readonly && this.props.loggedInUserType !== ELoggedInUserType.Employee)
        let totalTime = 0
        let totalPay = 0
        let taxablePay = 0
        this.state.expenses.forEach(item => {
            if (item.IsTime) {
                totalTime += item.Amount
            }
            const itemPay = item.IsTime ?
                (item.Classification && item.Classification.PayAmount ? item.Classification.PayAmount : 0) :
                item.Amount
            totalPay += itemPay
            taxablePay += item.Taxable ? itemPay : 0
        })

        return (
            <div>
                {!readonly && (this.props.loggedInUserType === ELoggedInUserType.Employee || permissionHelper.hasPermission(AuthenticationStore.Permissions.Expense.AddEmployeeExpense)) &&
                    <FloatingActionButton data-intercom-target={"menu-Employees-Expenses-Btn-Add"} className="fixed-add" style={{ position: "relative", float: "right", top: -45, right: 50, zIndex: 900 }} secondary={true} onClick={() => this.setState({ openAddExpenseDialog: true })}>
                        <ContentAdd />
                    </FloatingActionButton>
                }
                <TableActionBar
                    searchText=""
                    rowSelectionCount={(this.props.loggedInUserType === ELoggedInUserType.Employee || permissionHelper.hasPermission(AuthenticationStore.Permissions.Expense.DeleteEmployeeExpense)) && this.state.selectedExpenses.length}
                    singleSelectedMessage={strings.Expense_Selected}
                    multipleSelectedMessage={strings.x_Expenses_Selected}
                    title={strings.Expenses}
                    onDeleteClick={() =>
                        AppStateStore.setConfirmDialogContent({
                            content: strings.Are_you_sure_you_want_to_delete_selected_expense,
                            confirm: () => expenseActions.deleteExpense(this.props.user, this.state.selectedExpenses.map(item => item.Id), this.getExpenses)
                        })
                    }
                />
                <PayPeriodSelector
                    referenceDate={this.state.referenceDate}
                    selectedPayGroupId={this.employeePayGroupId}
                    handleChangeDate={referenceDate => this.setState({ referenceDate }, () =>
                        this.getAllData()
                    )}
                />
                <div>
                    {this.state.loading ?
                        <div className="spinner"><CircularProgress /></div> :
                        !this.state.expenses.length ?
                            <h2 style={{ textAlign: "center", marginTop: 25 }}>
                                {strings.No_expense_found}
                            </h2> :
                            <div style={{ marginTop: -20 }}>
                                <div style={{ textAlign: "right", paddingLeft: 10, paddingRight: 10, height: 48 }}>
                                    {!readonly && this.state.selectedExpenses.length > 0 &&
                                        (this.props.loggedInUserType === ELoggedInUserType.Employee || permissionHelper.hasPermission(AuthenticationStore.Permissions.Expense.ApproveEmployeeExpense)) && [
                                            <IconButton
                                                data-intercom-target={"menu-Employees-Expenses-Btn-ApproveExpense"}
                                                key="approve"
                                                onClick={() => this.handleUpdateApproveStatus(EApproveStatus.Approved)}
                                            >
                                                <FontIcon className="fa fa-check" color={grey700} />
                                            </IconButton>,
                                            <IconButton
                                                data-intercom-target={"menu-Employees-Expenses-Btn-UnApproveExpense"}
                                                key="unapprove"
                                                onClick={() => this.handleUpdateApproveStatus(EApproveStatus.Unapproved)}
                                            >
                                                <FontIcon className="fa fa-remove" color={grey700} />
                                            </IconButton>
                                        ]}
                                </div>
                                <UserDetailExpensesTable
                                    showExported={false}
                                    readonly={readonly}
                                    nonSelectable={this.props.loggedInUserType === ELoggedInUserType.Employee ?
                                        false :
                                        !permissionHelper.hasPermission(AuthenticationStore.Permissions.Expense.DeleteEmployeeExpense) &&
                                        !permissionHelper.hasPermission(AuthenticationStore.Permissions.Expense.ApproveEmployeeExpense)
                                    }
                                    expenses={this.state.expenses}
                                    selectedExpenses={this.state.selectedExpenses}
                                    handleSelect={selectedExpenses => this.setState({ selectedExpenses: selectedExpenses as IExpenseDocument[] })}
                                    handleUpdate={editExpense =>
                                        (this.props.loggedInUserType === ELoggedInUserType.Employee ?
                                            !editExpense.SupervisorApproved :
                                            permissionHelper.hasPermission(AuthenticationStore.Permissions.Expense.EditEmployeeExpense)) &&
                                        this.setState({ openAddExpenseDialog: true, editExpense: editExpense as IExpenseDocument })
                                    }
                                />
                                <div style={{ fontSize: 15, marginTop: 20 }}>
                                    <div style={{ fontWeight: 500 }}>{strings.Total}</div>
                                    <hr />
                                    <div>
                                        <div style={{ display: "flex" }}>
                                            <div style={{ display: "flex" }}>
                                                <div style={{ fontWeight: 500, width: 150 }}>{`${strings.Taxable}:`}</div>
                                                <div style={{ fontSize: 14 }}>{utils.getCurrencyFormat(taxablePay)}</div>
                                            </div>
                                            <div style={{ display: "flex", marginLeft: 200 }}>
                                                <div style={{ fontWeight: 500, width: 150 }}>{`${strings.TotalHours}:`}</div>
                                                <div style={{ fontSize: 14 }}>{totalTime}</div>
                                            </div>
                                        </div>
                                        <div style={{ display: "flex" }}>
                                            <div style={{ fontWeight: 500, width: 150 }}>{`${strings.Non_Taxable}:`}</div>
                                            <div style={{ fontSize: 14 }}>{utils.getCurrencyFormat(totalPay - taxablePay)}</div>
                                        </div>
                                        <div style={{ display: "flex" }}>
                                            <div style={{ fontWeight: 500, width: 150 }}>{`${strings.Total}:`}</div>
                                            <div style={{ fontSize: 14 }}>{utils.getCurrencyFormat(totalPay)}</div>
                                        </div>
                                    </div>
                                    <hr />
                                </div>
                            </div>
                    }
                </div>
                {this.state.openAddExpenseDialog &&
                    <AddUserDetailExpenseDialog
                        loggedInUserType={this.props.loggedInUserType}
                        employee={this.props.user}
                        payrollDataForPayGroup={{
                            PayrollHierarchyItemId: this.props.user.PayrollHierarchyItemId,
                            PayrollPayGroupId: this.props.user.PayrollPayGroupId
                        }}
                        payPeriodReferenceDate={this.state.referenceDate}
                        editExpense={this.state.editExpense}
                        handleClose={() => this.setState({ openAddExpenseDialog: false, editExpense: null })}
                        handleAdd={expenseBundle => expenseActions.addExpense(this.props.user, expenseBundle, this.getExpenses)}
                        handleUpdate={expenseBundle => expenseActions.updateExpense(this.props.user, expenseBundle, this.getExpenses)}
                        payDate={null}
                        payrollHierarchyId={null}
                        payrollPayGroupId={null}
                    />
                }
            </div>
        )
    }
}